import { Button, ButtonLevel, ButtonSize, SelectedFileStatus, Text, TextColor, TextSize } from '@vizit/atoms';
import { ChevronDownIcon, XCloseIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { useState } from 'react';
import { useUploadQueue } from './provider';
import { pickListedItems } from './provider/upload-queue.state';
import { UploadQueueItem } from './upload-queue-item';

interface UploadQueueProps extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Displays the upload queue in the lower right corner
 */
function UploadQueue(props: UploadQueueProps) {
  const { className, ...other } = props;
  const { items, clearedCount, clearedFailedCount, onRemoveItem, onClearCompleted } = useUploadQueue();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const completedCount =
    clearedCount +
    items.filter((item) => item.status === SelectedFileStatus.Success || item.status === SelectedFileStatus.Error)
      .length;
  const totalCount = clearedCount + items.length;

  const uploadingCount = items.filter((item) => item.status === SelectedFileStatus.Uploading).length;

  const pendingCount = items.filter((item) => item.status === SelectedFileStatus.Ready).length;

  const listedItems = pickListedItems(items);
  const unlistedCount = (status: SelectedFileStatus) =>
    items.filter((item) => item.status === status).length - listedItems.filter((item) => item.status === status).length;
  const files = (count: number) => (count === 1 ? 'file' : 'files');
  // Unlisted uploaded files need no line: the header counts them as done.
  const unlistedPendingCount = unlistedCount(SelectedFileStatus.Ready);
  const unlistedFailedCount = unlistedCount(SelectedFileStatus.Error);
  const unlistedNotes = [
    unlistedPendingCount > 0 && `${unlistedPendingCount} more ${files(unlistedPendingCount)} waiting to upload`,
    unlistedFailedCount > 0 && `${unlistedFailedCount} more ${files(unlistedFailedCount)} failed to upload`,
  ].filter(Boolean);

  if (items.length === 0) {
    return null;
  }

  const getStatusText = () => {
    const inProgressCount = uploadingCount + pendingCount;
    if (inProgressCount > 0) {
      return `Uploading ${completedCount} of ${totalCount} assets`;
    }
    if (completedCount === totalCount) {
      const failedCount = clearedFailedCount + items.filter((item) => item.status === SelectedFileStatus.Error).length;
      const uploadedCount = completedCount - failedCount;
      if (failedCount === 0) {
        return `${uploadedCount} asset${uploadedCount !== 1 ? 's' : ''} uploaded`;
      }
      if (uploadedCount === 0) {
        return `${failedCount} asset${failedCount !== 1 ? 's' : ''} failed to upload`;
      }
      return `${uploadedCount} uploaded, ${failedCount} failed to upload`;
    }
    return `${totalCount} asset${totalCount !== 1 ? 's' : ''} queued`;
  };

  const handleClose = () => {
    onClearCompleted();
  };

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => !prev);
  };

  return (
    <div
      className={cx(
        'fixed bottom-4 right-4 z-[9999] flex flex-col bg-white shadow-lg border border-secondary min-w-[480px] max-w-[480px]',
        className,
      )}
      aria-label="Upload queue"
      {...other}
    >
      <div className="flex items-center justify-between pl-8 pr-2 py-4 border-b border-secondary bg-secondary">
        <Text color={TextColor.Secondary} size={TextSize.Sm}>
          {getStatusText()}
        </Text>
        <div className="flex items-center">
          <Button
            aria-label={isCollapsed ? 'Expand queue' : 'Collapse queue'}
            level={ButtonLevel['Text-Secondary']}
            size={ButtonSize.Sm}
            iconStart={
              <ChevronDownIcon
                size={20}
                className={cx('transition-transform duration-200', isCollapsed && 'rotate-180')}
              />
            }
            onPress={toggleCollapsed}
          />
          <Button
            aria-label="Close upload queue"
            level={ButtonLevel['Text-Secondary']}
            size={ButtonSize.Sm}
            iconStart={<XCloseIcon size={20} />}
            onPress={handleClose}
          />
        </div>
      </div>
      {!isCollapsed && (
        <>
          <div className="flex flex-col overflow-y-auto max-h-[400px]">
            {listedItems.map((item) => (
              <div key={item.id} className="animate-in slide-in-from-right-full duration-300">
                <UploadQueueItem item={item} onRemove={onRemoveItem} />
              </div>
            ))}
          </div>
          {unlistedNotes.length > 0 && (
            <Text color={TextColor.Tertiary} size={TextSize.Sm} className="px-8 py-4 border-t border-secondary">
              {unlistedNotes.join(' · ')}
            </Text>
          )}
        </>
      )}
    </div>
  );
}

export { UploadQueue };
export type { UploadQueueProps };
